import { useEffect, useMemo } from "react";
import { Check, Pill, TimerReset, X } from "lucide-react";
import { Button } from "./ui/button";
import type { LocalMedication } from "../services/MedicationService";
import medicationService from "../services/MedicationService";
import { cn } from "../lib/utils";
import {
  measureCardMeta,
  measureCardTitle,
  measureCardValue,
  measureEyebrow,
  measureSectionTitle,
} from "../utils/measurePageTypography";

export type MedicationPastListSheetProps = {
  open: boolean;
  onClose: () => void;
  medications: LocalMedication[];
  loading?: boolean;
  markingTimingId?: string | null;
  onMarkTaken?: (timingId: string) => void;
  onAddMedication?: () => void;
};

const toDisplayName = (name: string) =>
  name.trim().replace(/\b\w/g, (c) => c.toUpperCase());

const formatDoseLabel = (frequency: string) => {
  const t = frequency?.trim();
  if (!t) return "—";
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const toneStyles = {
  due: {
    card: "border-border/80 bg-card",
    icon: "border-sky-200/70 bg-sky-50/90 dark:border-sky-900/45 dark:bg-sky-950/35",
    pill: "text-primary",
    status: "text-amber-700 dark:text-amber-400",
  },
  on_time: {
    card: "border-emerald-200/80 bg-emerald-50/30 dark:border-emerald-900/40 dark:bg-emerald-950/20",
    icon: "border-emerald-200/70 bg-emerald-50/90 dark:border-emerald-900/45 dark:bg-emerald-950/40",
    pill: "text-emerald-600 dark:text-emerald-400",
    status: "text-emerald-800 dark:text-emerald-300",
  },
  early: {
    card: "border-sky-200/80 bg-sky-50/25 dark:border-sky-900/40 dark:bg-sky-950/20",
    icon: "border-sky-200/70 bg-sky-50/90 dark:border-sky-900/45 dark:bg-sky-950/40",
    pill: "text-sky-600 dark:text-sky-400",
    status: "text-sky-800 dark:text-sky-300",
  },
  overdue: {
    card: "border-orange-200/80 bg-orange-50/30 dark:border-orange-900/40 dark:bg-orange-950/20",
    icon: "border-orange-200/70 bg-orange-50/90 dark:border-orange-900/45 dark:bg-orange-950/40",
    pill: "text-orange-600 dark:text-orange-400",
    status: "text-orange-800 dark:text-orange-300",
  },
} as const;

const MedicationPastListSheet = ({
  open,
  onClose,
  medications,
  loading = false,
  markingTimingId = null,
  onMarkTaken,
  onAddMedication,
}: MedicationPastListSheetProps) => {
  const sortedMeds = useMemo(() => {
    const due = medications.filter((m) => m.status !== "taken");
    const taken = medications.filter((m) => m.status === "taken");

    due.sort((a, b) => {
      const dateCmp = (a.date ?? "").localeCompare(b.date ?? "");
      if (dateCmp !== 0) return dateCmp;
      return (a.time ?? "").localeCompare(b.time ?? "");
    });

    taken.sort((a, b) => {
      const aKey = a.taken_at ?? `${a.date ?? ""}T${a.time ?? ""}`;
      const bKey = b.taken_at ?? `${b.date ?? ""}T${b.time ?? ""}`;
      return bKey.localeCompare(aKey);
    });

    return [...due, ...taken];
  }, [medications]);

  const dueCount = medications.filter((m) => m.status !== "taken").length;
  const takenCount = medications.filter((m) => m.status === "taken").length;
  const overdueCount = medications.filter(
    (m) => m.status !== "taken" && (m.adherence === "overdue" || (m.diff_minutes ?? 0) > 0),
  ).length;

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="med-past-sheet-title"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />

      <div className="relative z-10 mx-auto w-full max-w-[400px] duration-200 animate-in slide-in-from-bottom-4">
        <div className="flex max-h-[min(92dvh,_820px)] flex-col overflow-hidden rounded-t-3xl border border-border bg-background shadow-2xl ring-1 ring-border/80">
          <div className="flex justify-center pb-2 pt-3">
            <div className="h-1 w-11 rounded-full bg-muted-foreground/25" aria-hidden />
          </div>

          <div className="shrink-0 border-b border-border/70 px-5 pb-4 pt-0.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className={measureEyebrow}>Medication · Log past</p>
                <h2 id="med-past-sheet-title" className={`mt-1 ${measureSectionTitle}`}>
                  All medications
                </h2>
                {!loading && medications.length > 0 ? (
                  <p className={`mt-1 ${measureCardMeta}`}>
                    {dueCount} due · {takenCount} taken
                    {overdueCount > 0 ? ` · ${overdueCount} overdue` : ""}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted"
                aria-label="Close"
              >
                <X className="h-5 w-5" strokeWidth={2} />
              </button>
            </div>
          </div>

          <div className="scrollbar-hide min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {loading ? (
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-[104px] animate-pulse rounded-2xl border border-border bg-muted/40"
                    aria-hidden
                  />
                ))}
              </div>
            ) : medications.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-muted/20 px-4 py-10 text-center">
                <Pill className="mx-auto h-8 w-8 text-muted-foreground/50" strokeWidth={1.5} />
                <p className="mt-3 text-sm font-medium text-foreground">No medications yet</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Add one below or use Log now on the card.
                </p>
              </div>
            ) : (
              <ul className="space-y-3">
                {sortedMeds.map((med) => {
                  const taken = med.status === "taken";
                  const tone = medicationService.getAdherenceTone(med);
                  const styles = toneStyles[tone];
                  const scheduledLine = medicationService.getScheduledLine(med);
                  const statusLine = medicationService.getMedicationStatusLine(med);
                  const marking = markingTimingId === med.timing_id;

                  return (
                    <li
                      key={med.timing_id}
                      className={cn("overflow-hidden rounded-2xl border shadow-sm", styles.card)}
                    >
                      <div className="flex items-start gap-3 p-4">
                        <span
                          className={cn(
                            "mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border",
                            styles.icon,
                          )}
                        >
                          <Pill className={cn("h-5 w-5", styles.pill)} strokeWidth={1.85} />
                        </span>

                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                            <span className={measureCardTitle}>
                              {toDisplayName(med.medication_name)}
                            </span>
                            <span
                              className={`${measureCardValue} line-clamp-2 max-w-[48%] text-right text-muted-foreground`}
                            >
                              {formatDoseLabel(med.frequency)}
                            </span>
                          </div>

                          {scheduledLine ? (
                            <p className="mt-1.5 text-xs text-muted-foreground">
                              {scheduledLine}
                            </p>
                          ) : null}

                          <p
                            className={cn(
                              "mt-1 text-compact-xs font-bold uppercase tracking-[0.06em]",
                              styles.status,
                            )}
                          >
                            {statusLine}
                          </p>
                        </div>

                        {taken ? (
                          <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-compact-xs font-bold uppercase tracking-wide text-white shadow-sm">
                            <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                            Taken
                          </span>
                        ) : onMarkTaken ? (
                          <button
                            type="button"
                            disabled={marking}
                            onClick={() => onMarkTaken(med.timing_id)}
                            className="shrink-0 rounded-full border border-border bg-foreground px-3 py-1.5 text-compact-xs font-bold uppercase tracking-wide text-background shadow-sm transition-opacity hover:bg-foreground/90 disabled:opacity-50"
                          >
                            {marking ? "…" : "Done"}
                          </button>
                        ) : (
                          <span className="flex shrink-0 items-center gap-1 rounded-full border border-amber-200/80 bg-amber-50 px-2.5 py-1 text-compact-xs font-bold uppercase tracking-wide text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-300">
                            <TimerReset className="h-3 w-3" />
                            Due
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="shrink-0 space-y-2.5 border-t border-dashed border-border bg-background px-5 py-4 pb-[max(1.25rem,env(safe-area-inset-bottom,1rem))]">
            {onAddMedication ? (
              <Button
                type="button"
                className="h-14 w-full rounded-xl bg-foreground text-base font-semibold text-background hover:bg-foreground/90"
                onClick={() => {
                  onClose();
                  onAddMedication();
                }}
              >
                Add medication
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              className="h-12 w-full rounded-xl border-border bg-card text-base font-medium"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicationPastListSheet;
